import { PropsWithChildren, createContext, useContext } from 'react'
import { ChevronDownIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'libs/i18n'
import { MoveIcon } from 'shared/atoms'
import { cn } from 'utils/style'
import { FLOOR_MAP_ITEM } from '../placement'

type OpenSection = {
	openSection: string | null
	toggleSection: (section: string) => void
}

export const OpenSectionContext = createContext<OpenSection>({
	openSection: null,
	toggleSection: () => undefined
})

type FieldProps = PropsWithChildren<{
	label: string
	section: string
	isPlaceable?: boolean
}>

export const fieldButtonClassName =
	'w-full rounded-md border border-dashed border-border px-2 py-2 text-xs font-medium text-foreground transition hover:bg-muted'

export const Field = ({ children, label, section, isPlaceable = false }: FieldProps) => {
	const { t } = useTranslation()
	const { openSection, toggleSection } = useContext(OpenSectionContext)
	const isOpen = openSection === section

	return (
		<div className="rounded-md border border-border">
			<button
				type="button"
				aria-expanded={isOpen}
				className="flex w-full items-center justify-between px-3 py-2 text-sm font-medium text-foreground"
				onClick={() => toggleSection(section)}>
				{label}
				<ChevronDownIcon
					className={cn(
						'h-[15px] w-[15px] stroke-[1.8] transition-transform',
						isOpen && 'rotate-180'
					)}
				/>
			</button>
			<div
				hidden={!isOpen}
				className="space-y-2 border-t border-border px-3 py-3 text-sm text-muted-foreground">
				{isPlaceable && (
					<p
						draggable
						className="flex cursor-grab items-center gap-2 active:cursor-grabbing"
						onDragStart={(event) => {
							event.dataTransfer.setData(FLOOR_MAP_ITEM, section)
							event.dataTransfer.effectAllowed = 'copy'
						}}>
						<MoveIcon className="h-3.5 w-3.5 shrink-0" />
						{t('Drag an item onto the map')}
					</p>
				)}
				{children}
			</div>
		</div>
	)
}
